import React, { useCallback, useEffect, useState } from "react";
import { DA_Input } from "@danishagro/shared/src/components/atoms/Input/Input.component";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import {
    DA_CheckboxList,
    DA_CheckboxListProps,
    DA_CheckboxType,
} from "@danishagro/shared/src/components/molecules/CheckboxList/CheckboxList.component";
import { DA_Spinner } from "@danishagro/shared/src/components/atoms/Spinner/Spinner.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { useInviteApi } from "@danishagro/sharedShop/src/hooks/api/useInviteApi.hook";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { isEmailValid } from "@danishagro/shared/src/helpers/validateEmail.helper";
import { useUserRolesApi } from "@danishagro/sharedShop/src/hooks/api/useUserRolesApi.hook";
import { useBottomSheet } from "@danishagro/shared/src/contexts/bottomSheet/bottomSheet.context";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import S from "./InviteUserBottomSheet.module.scss";

interface InviteUserModalProps {
    accounts?: DA_CheckboxListProps["checkboxList"];
    cvr: string;
    onSubmit?: () => void;
    alias?: string;
}

export const _InviteUserBottomSheet = ({ cvr, onSubmit, alias }: InviteUserModalProps) => {
    const { getDictionaryItem, getDictionaryString } = useTranslations();
    const { inviteUser } = useInviteApi();
    const { getInviteAccounts } = useUserRolesApi();
    const { dismissBottomSheet } = useBottomSheet();
    const { currentCulture, customerNumber } = useAppData();
    const [email, setEmail] = useState("");
    const [culture, _setCulture] = useState(currentCulture);
    const [accounts, setAccounts] = useState<DA_CheckboxType[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [successText, setSuccessText] = useState(false);
    const [loading, setLoading] = useState(true);
    const inviteType = getDictionaryString(alias);
    const [isValid, setIsValid] = useState(false);

    const submit = useCallback(() => {
        setSubmitting(true);

        inviteUser({
            culture,
            email,
            roleAlias: alias,
            customerNumbers: accounts
                .filter((account) => account.checked)
                .map((account) => account.value),
        })
            .then(() => {
                setSuccessText(true);
                setSubmitting(false);
                onSubmit();
            })
            .catch((err) => {
                console.log(err);
            });
    }, [inviteUser, culture, email, alias, accounts, onSubmit]);

    useEffect(() => {
        getInviteAccounts(customerNumber)
            .then((response) => {
                setAccounts(
                    response.map(({ name, number }) => ({
                        label: `${number} - ${name}`,
                        name: "invite-accounts",
                        value: number,
                        checked: false,
                    }))
                );
            })
            .finally(() => {
                setLoading(false);
            });
    }, [customerNumber, cvr, getInviteAccounts]);

    useEffect(() => {
        const selectedAccounts = accounts.filter((item) => item.checked);
        const emailIsValid = isEmailValid(email);

        if (selectedAccounts.length && emailIsValid) {
            setIsValid(true);
        } else {
            setIsValid(false);
        }
    }, [email, accounts]);

    return (
        <>
            {successText ? (
                <div className={S.wrapper}>
                    <div className={S.iconWrapper}>
                        <DA_Icon name={DA_IconNames.Checkmark} className={S.icon} />
                    </div>
                    <DA_Text alignCenter bold>
                        {getDictionaryItem("successTitle", { email: email })}
                    </DA_Text>
                    <DA_Text alignCenter>{getDictionaryItem("successText")}</DA_Text>
                    <DA_Button onClick={dismissBottomSheet} title={getDictionaryString("close")} />
                </div>
            ) : (
                <>
                    <DA_Input
                        label={getDictionaryItem("email", { uppercaseFirst: true })}
                        id="invite-email"
                        type="email"
                        value={email}
                        onChange={(newEmail) => setEmail(newEmail)}
                    />

                    <DA_CheckboxList
                        className={S.checkboxList}
                        title={getDictionaryItem("chooseAccount", {
                            uppercaseFirst: true,
                        })}
                        checkboxList={accounts}
                        isLoading={loading}
                        onChange={(newAccounts) => setAccounts(newAccounts)}
                    />

                    <DA_Text>
                        {getDictionaryItem("inviteUserDisclaimer", { type: inviteType })}
                    </DA_Text>

                    {!submitting && !successText && (
                        <div className={S.buttonWrapper}>
                            <DA_Button
                                title={getDictionaryString("inviteUser", {
                                    uppercaseFirst: true,
                                })}
                                onClick={submit}
                                className={S.primary}
                                disabled={!isValid}
                            />
                            <DA_Button
                                title={getDictionaryString("cancel")}
                                isGhost
                                onClick={dismissBottomSheet}
                                className={S.secondary}
                            />
                        </div>
                    )}
                    {submitting && <DA_Spinner className={S.spinner} />}
                </>
            )}
        </>
    );
};
